import {
  ChannelDetails,
  ChannelTypeEnum,
  getChannelById,
  getLinkedWorkspaces,
  getUserData,
  IssueStatus,
  Principal,
  store,
  ThreadListTags,
  ThreadTagListItem,
  ThreadTagTypes,
} from '@zonofi/seller-rtk-store';
import { PNChannelMetaData } from '../types';
import { camelCase, isNil, isString, omitBy, startCase } from 'lodash';
import { ChatSVGS } from '../assets';

export const formatChannelMetaData = (
  channelMetaData: PNChannelMetaData
): Partial<ChannelDetails> => {
  const linkedWorkspaces = getLinkedWorkspaces(store.getState()) || [];

  const {
    title,
    type,
    description,
    sellerWorkspaceId = '',
    id,
    customer_id,
    customer_code,
    customer_name,
    customer_companyName,
    tags,
    issueStatus,
    cSubType,
    cType,
    conversationTags,
    ...restMetaData
  } = channelMetaData || {};

  const seller = linkedWorkspaces.find(
    (x) => x.principalWorkspaceId === sellerWorkspaceId
  );

  const channelType = getTypeFromCustomMetadata(
    title === 'zo.ai' ? 'zo.ai' : type
  );
  const channelNameData = title || description;
  const { channelName, profilePic } = getNameFromCustomMetadata(
    channelNameData,
    type,
    seller
  );

  const channel = getChannelById(id)(store.getState());
  const { customerId: customerIdFromChannelData } = channel || {};

  const customerId = customer_id || customerIdFromChannelData;
  const customerName = customer_name;
  const companyName = getCompanyName(customer_companyName, seller);
  const threadTags =
    type === 'issuethread'
      ? getIssueTagList(issueStatus)
      : tags
        ? getCustomerTagList(tags)
        : [];

  const channelData = {
    ...restMetaData,
    type: channelType,
    channelName,
    profilePic,
    customerId,
    customerName,
    companyName,
    sellerWorkspaceId,
    id,
    issueStatus,
    threadTags,
    channelSubType: cSubType,
    channelType: cType,
    customerCode: customer_code,
    conversationTags: convertConversationTagsToThreadTagList(
      conversationTags || []
    ),
  };

  const cleanedChannelData = omitBy(channelData, isNil);

  return cleanedChannelData;
};

const getTypeFromCustomMetadata = (type: string): string => {
  switch (type) {
    case 'zo.ai':
      return ChannelTypeEnum.ZOAI;
    case 'groupthread':
      return ChannelTypeEnum.GROUPTHREAD;
    case 'issuethread':
      return ChannelTypeEnum.ISSUETHREAD;
    case 'customerthread':
      return ChannelTypeEnum.CUSTOMERTHREAD;
    default:
      return ChannelTypeEnum.THREAD;
  }
};

const getNameFromCustomMetadata = (
  channelName: string,
  type: string,
  seller?: Principal
): {
  channelName: string;
  profilePic?: string;
} => {
  const user = getUserData(store.getState());
  const userName = user?.firstName;
  if (seller && type === 'customerthread')
    return {
      channelName: seller.spaceName,
      profilePic: seller.profilePic,
    };

  if (type === 'groupthread') {
    return {
      channelName: channelName
        ?.split(',')
        .filter((x) => x !== userName)
        .join(','),
    };
  }
  return { channelName };
};

const getCompanyName = (companyName: string, seller?: Principal): string => {
  if (seller) {
    return seller?.spaceName || '-';
  }
  return companyName;
};

const tagsColorMap: Record<
  ThreadTagTypes,
  {
    color: string;
    backgroundColor: string;
  }
> = {
  order: {
    color: '#8c5e00',
    backgroundColor: '#fff3dc',
  },
  invoice: {
    color: '#00398c',
    backgroundColor: '#dbe9ff',
  },
  priceList: {
    color: '#0099cc',
    backgroundColor: '#d9e9ff',
  },
  campaign: {
    color: '#006600',
    backgroundColor: '#d6f4d6',
  },
  payment: {
    color: '#186846',
    backgroundColor: '#def8ed',
  },
  dispatch: {
    color: '#370085',
    backgroundColor: '#e8d8ff',
  },
};

export const getCustomerTagList = (
  tags: string | ThreadListTags[]
): ThreadTagListItem[] => {
  let customerTags: ThreadListTags[] = [];
  if (isString(tags)) customerTags = JSON.parse(tags) as ThreadListTags[];
  else customerTags = tags;
  return customerTags.map(({ type, value }) => ({
    text: `${startCase(camelCase(type))} - ${startCase(camelCase(value))}`,
    ...tagsColorMap[type],
  }));
};

const conversationTagsColorMap: Record<
  string,
  {
    color: string;
    backgroundColor: string;
  }
> = {
  // Order related tags
  create_order: {
    color: '#8c5e00',
    backgroundColor: '#fff3dc',
  },
  modify_order: {
    color: '#8c5e00',
    backgroundColor: '#fff3dc',
  },
  cancel_order: {
    color: '#cc6600',
    backgroundColor: '#ffe6d9',
  },
  order_enquiry: {
    color: '#8c5e00',
    backgroundColor: '#fff3dc',
  },
  // Enquiry related tags
  product_enquiry: {
    color: '#0099cc',
    backgroundColor: '#d9e9ff',
  },
  delivery_enquiry: {
    color: '#370085',
    backgroundColor: '#e8d8ff',
  },
  // Inventory related tags
  stock_update: {
    color: '#006666',
    backgroundColor: '#d6f4f4',
  },
  // Pricing/Negotiation tags
  negotiation: {
    color: '#996600',
    backgroundColor: '#fff0d9',
  },
  discount: {
    color: '#996600',
    backgroundColor: '#fff0d9',
  },
  // Payment related tags
  payment: {
    color: '#186846',
    backgroundColor: '#def8ed',
  },
  // Invoice related tags
  invoice: {
    color: '#00398c',
    backgroundColor: '#dbe9ff',
  },
  // Accounting tags
  ledger: {
    color: '#004d66',
    backgroundColor: '#d9f0f7',
  },
  // Return/Refund tags
  return: {
    color: '#8c3900',
    backgroundColor: '#ffe6d9',
  },
  credit_note: {
    color: '#8c3900',
    backgroundColor: '#ffe6d9',
  },
  refund: {
    color: '#8c3900',
    backgroundColor: '#ffe6d9',
  },
  // Support related tags
  issue: {
    color: '#cc0000',
    backgroundColor: '#ffe6e6',
  },
  support_request: {
    color: '#cc0000',
    backgroundColor: '#ffe6e6',
  },
  query: {
    color: '#005c99',
    backgroundColor: '#d9ecf7',
  },
};

export const convertConversationTagsToThreadTagList = (
  conversationTags: string | string[]
): ThreadTagListItem[] => {
  const cTags =
    typeof conversationTags === 'string'
      ? conversationTags.split(',')
      : conversationTags;
  return cTags.map((tag) => {
    // Convert snake_case to Start Case (e.g., "create_order" -> "Create Order")
    const formattedText = startCase(tag.replace(/_/g, ' '));

    // Get color from map or use default
    const colorData = conversationTagsColorMap[tag] || {
      color: '#666666',
      backgroundColor: '#f0f0f0',
    };

    return {
      text: formattedText,
      ...colorData,
    };
  });
};

const issueStatusMap: Partial<Record<IssueStatus, ThreadTagListItem>> = {
  [IssueStatus.CLOSED]: {
    text: 'Closed',
    color: '#1D8459',
    backgroundColor: '#DEF8ED',
    icon: ChatSVGS.CheckMark,
  },
  [IssueStatus.ASSIGNED]: {
    text: 'Assigned',
    color: '#002966',
    backgroundColor: '#DBE9FF',
    icon: ChatSVGS.VerifiedAccount,
  },
};

const getIssueTagList = (issueStatus?: IssueStatus): ThreadTagListItem[] => {
  return issueStatus && issueStatusMap[issueStatus]
    ? [issueStatusMap[issueStatus]]
    : [];
};
